/**
 * What this is called, and the only place it is written.
 *
 * There are TWO names, because there are two things:
 *
 * - **The platform** is `Adanim` — the entity, the repository, the login and
 *   billing everything shares, the "powered by" mark. It is the thing that
 *   hosts more than one product.
 * - **A product** is what a person actually uses. `ShulBase` is the
 *   institutional one — members, the luach, messaging — and the one a bare
 *   `APP_NAME` means; the others answer on hosts of their own.
 *
 * Conflating them is what made the rename necessary: a platform named after its
 * first product has nowhere to put its second. Reach for `PLATFORM_NAME` where
 * you mean the company or the codebase, and `APP_NAME` where a person is
 * reading it.
 *
 * There is more than one product, so `PRODUCTS` is the registry and a **host** is what
 * decides which one a person is looking at (`productFor`). The registry is also
 * where a product's mail domain lives, so an email's sender and its links are
 * resolved from the same row rather than agreeing by hand.
 *
 * A product is the same word in the tree as on the screen, so there is nothing
 * to map through. What a person READS still comes from here rather than being
 * typed: `productName` is what a screen calls.
 *
 * `check-rules` enforces that the literal appears nowhere else in code. A brand
 * module nobody is obliged to use collects leaked literals until the rename it
 * existed to make cheap is a hunt through twenty files.
 *
 * ## What must NOT be renamed
 *
 * Some strings contain a name and are not the brand. They are protocol — frozen
 * the moment something outside this codebase depended on them:
 *
 * - **HMAC namespaces** (`…:pay-token:v1`). The namespace is part of the
 *   signature, so changing it invalidates every link already in somebody's
 *   inbox. They carry an explicit `:v1` for the day that is intended.
 * - **Wire keys** shared with a client, **env var names** read from a
 *   developer's own `.env`, the registered **domain**, and the **build config**,
 *   which runs in Node before the bundler exists and cannot import this file.
 *
 * Those are listed as named exceptions in `scripts/check-rules.ts`, each with
 * the reason it is frozen. Renaming one is a migration, not a find-and-replace.
 */

import type { OrgKind } from './org-kind'

const envName =
  typeof process !== 'undefined' ? process.env?.APP_NAME : undefined

/** The platform: the entity, the codebase, everything the products share. */
export const PLATFORM_NAME = 'Adanim'

/** Machine form of the platform's name. */
export const PLATFORM_SLUG = 'adanim'

/** The database setting that identifies a database provisioned on this machine. */
export const LOCAL_DATABASE_MARKER = `${PLATFORM_SLUG}.local`

/** Where the platform itself answers: its own site, and the SSO exchange. */
export const PLATFORM_HOST = 'adanim.co'

/** Which product a surface belongs to. */
export type Product = 'shulbase' | 'studio' | 'terumah'

/** What a product is: what it is called, where it answers, what it sends as. */
export interface ProductBrand {
  name: string
  slug: Product
  /** The registered host it is served on. */
  host: string
  tagline: string
  /**
   * The domain its mail is sent from. Its own where that domain holds a mail
   * identity, so a message says who wrote it; otherwise the platform's, with
   * the product's name in front of the address saying which.
   */
  mailDomain: string
  /**
   * The sentence its site's foot carries: what that product is answerable for.
   *
   * Here rather than in the chrome that draws it, for the reason the whole
   * module exists: it names the product, and a name written outside this file
   * is a name that survives the rename it was supposed to make cheap.
   */
  footNote: string
}

/**
 * The products this platform hosts.
 *
 * A product is what a person actually uses; the platform is what they share.
 * A product's `slug` is its name in the tree as well, so the context, the
 * package and the routes all say the word this row does.
 */
export const PRODUCTS: Record<Product, ProductBrand> = {
  shulbase: {
    name: 'ShulBase',
    slug: 'shulbase',
    host: 'shulbase.com',
    tagline: 'Synagogue management, simplified.',
    mailDomain: 'shulbase.com',
    footNote:
      'Zmanim computed with kosher-zmanim and @hebcal/core. Times are halachic times people rely on — every rule carries its reasoning.',
  },
  studio: {
    name: 'Studio',
    slug: 'studio',
    host: 'adanim.studio',
    tagline: 'The creative place for the design of all media.',
    mailDomain: 'adanim.studio',
    footNote:
      'Seforim, pages, decks, songs, covers and type — each held as data, designed in one place, and printed or published from the same source.',
  },
  /*
   * Mails from the platform's domain: that identity is verified, and its own
   * domain holds no mail identity of its own. The name in front of the address
   * is Terumah's either way.
   */
  terumah: {
    name: 'Terumah',
    slug: 'terumah',
    host: 'terumah.co',
    tagline: 'Giving, for the causes people give to.',
    mailDomain: 'adanim.co',
    footNote:
      'Every gift is recorded the moment it arrives and acknowledged only once it has.',
  },
}

/** The same sentence for the platform's own site, which hosts rather than does. */
export const PLATFORM_FOOT_NOTE = `${PLATFORM_NAME} hosts ${PRODUCTS.shulbase.name}, ${PRODUCTS.terumah.name} and ${PRODUCTS.studio.name} — one account, one design, separate products.`

/**
 * The institutional product — the one a bare `APP_NAME` means.
 *
 * Its literal type is kept, not widened to `Product`, so it can stand in for
 * the name anywhere the name would otherwise be written out: a key, a lookup,
 * a default argument. That is what lets the rest of the tree obey the rule
 * this module exists for.
 */
export const DEFAULT_PRODUCT = 'shulbase' as const satisfies Product

/**
 * Which product answers on a host, or null where none does.
 *
 * A subdomain counts, so `studio.localhost` in development and a future
 * `www.` resolve to the same product as the bare host. Null is the honest
 * answer for the platform's own host and for an org's custom domain: neither
 * is a product, and guessing one would put a product's chrome over a
 * congregation's own site.
 */
export function productFor(host: string): Product | null {
  const h = host.toLowerCase().split(':')[0] ?? ''
  for (const p of Object.values(PRODUCTS)) {
    if (h === p.host || h.endsWith(`.${p.host}`)) return p.slug
    // Development serves every product off one origin, told apart by a
    // subdomain of localhost — which browsers resolve without a hosts file. It
    // is keyed on the SLUG rather than on the host's first label, so a product
    // whose host is a subdomain of the platform's still has a name of its own
    // to be reached by.
    if (h === `${p.slug}.localhost` || h === `${APP_LABEL}.${p.slug}.localhost`)
      return p.slug
  }
  return null
}

/** Whether a host may serve a preview owned by a non-Studio product. */
export function acceptsNonStudioPreviewHost(host: string): boolean {
  return productFor(host) !== 'studio'
}

/** Where a product answers. */
export function hostFor(product: Product): string {
  return PRODUCTS[product].host
}

/**
 * The label a product's signed-in app answers under, in front of its host.
 *
 * A product has two faces on two hosts: the public site at its own host, which
 * serves anybody whether or not they are signed in, and the app at
 * `app.<host>`, where its sessions live. Keeping them apart is what lets
 * somebody signed in still read a congregation's public pages — one host
 * cannot be both a public page and a back office at the same address.
 *
 * The same label in development (`app.shulbase.localhost`), so the split is
 * exercised locally rather than first met in production.
 */
export const APP_LABEL = 'app'

/**
 * A product's app host, from the host its public site answers on — the one
 * derivation, so the API building a link and the page sending somebody there
 * cannot disagree about where the app is.
 *
 * The bare loopback serves the institutional product, so its app is reached
 * under that product's own dev name: `app.localhost` would name no product.
 */
export function appHostFor(host: string): string {
  const h = host.toLowerCase()
  return `${APP_LABEL}.${h === 'localhost' ? `${DEFAULT_PRODUCT}.localhost` : h}`
}

/** Whether a host is a product's app rather than its public site. */
export function isAppHost(host: string): boolean {
  const h = host.toLowerCase().split(':')[0] ?? ''
  if (!h.startsWith(`${APP_LABEL}.`)) return false
  const rest = h.slice(APP_LABEL.length + 1)
  return Object.values(PRODUCTS).some(
    (p) => rest === p.host || rest === `${p.slug}.localhost`,
  )
}

/**
 * Which face of Studio a host is, or null if it is not Studio.
 *
 * Public tickets and unauthenticated link reads belong to the public face;
 * signed-in preview tickets belong to the app. Any other host is not Studio.
 */
export function studioFace(host: string): 'public' | 'app' | null {
  if (productFor(host) !== 'studio') return null
  return isAppHost(host) ? 'app' : 'public'
}

/**
 * Whether a host is the platform's own — its site, and the SSO exchange.
 *
 * Development reaches it at `platform.localhost` rather than at the platform's
 * slug, so the dev host reads as the thing it serves. Every product's dev host
 * is its own slug (see `productFor`), and nothing is served at the bare
 * `localhost`, which stays the institutional product.
 */
export function isPlatformHost(host: string): boolean {
  const h = host.toLowerCase().split(':')[0] ?? ''
  // A product may answer under the platform's domain, and is still the product.
  if (productFor(h)) return false
  return (
    h === PLATFORM_HOST ||
    h.endsWith(`.${PLATFORM_HOST}`) ||
    h === 'platform.localhost'
  )
}

/**
 * The product an org works in, from the kind of org it is.
 *
 * Derived rather than stored: the kind is already the real fact about the org,
 * and a second column naming its product is a second answer that can disagree
 * with the first. A charity gives through Terumah; every other kind is the
 * institutional product until somebody decides otherwise, which is one line
 * here rather than a migration.
 */
export function productForOrgKind(
  // Session and public-view boundaries still carry the persisted enum as an
  // unvalidated string; unknown values safely stay on the default product.
  kind: string,
): typeof DEFAULT_PRODUCT | 'terumah' {
  return kind === 'chesed' ? 'terumah' : DEFAULT_PRODUCT
}

/**
 * Each product's public directory. Null means that product has no directory.
 *
 * Path, listed kind, starting choice and link words are one product rule, kept
 * together so no host can advertise a directory it does not answer.
 */
export interface ProductDirectory {
  path: string
  kind: OrgKind
  startsListed: boolean
  allLabel: string
  navLabel: string
}

export const PRODUCT_DIRECTORIES: Record<Product, ProductDirectory | null> = {
  shulbase: {
    path: 'shuls',
    kind: 'shul',
    startsListed: true,
    allLabel: 'All congregations',
    navLabel: 'Shuls',
  },
  terumah: {
    path: 'orgs',
    kind: 'chesed',
    startsListed: false,
    allLabel: 'All organizations',
    navLabel: 'Organizations',
  },
  studio: null,
}

/** The public-directory rule for an org kind, or null when it has none. */
export function directoryForOrgKind(kind: OrgKind): ProductDirectory | null {
  const directory = PRODUCT_DIRECTORIES[productForOrgKind(kind)]
  return directory?.kind === kind ? directory : null
}

/**
 * The product a person is looking at.
 *
 * The default is the institutional product, which is what an unqualified call
 * site means: a server composing a shul's statement, a script naming the app.
 * A surface that knows better passes the product it resolved from its host.
 */
export function productName(product: Product = DEFAULT_PRODUCT): string {
  return product === DEFAULT_PRODUCT ? APP_NAME : PRODUCTS[product].name
}

/** A site of ours: a product, or the platform itself. */
export type MailSite = Product | 'platform'

/**
 * The local part every address of ours uses.
 *
 * One word for every site, because the mailbox is not the point: what a
 * recipient reads is the name in front of it, and the domain already says which
 * of our sites wrote. A per-site local part would be a second thing to keep in
 * step with the first for no gain.
 */
const MAIL_LOCAL_PART = 'noreply'

/**
 * Where a site's own mail comes from — its name and its address together.
 *
 * DERIVED, not configured. A product already declares the domain it answers on
 * and the domain its mail comes from, so an address is a fact about the
 * registry rather than a value somebody types into a settings screen and
 * eventually forgets: the failure that produces is a Studio message arriving
 * from a congregation's address, which is a message a recipient is right not to
 * trust.
 *
 * An install may still name a sender of its own per site, and does so where the
 * mailbox has to be somewhere else. This is what it falls back to.
 */
export function mailSender(site: MailSite): string {
  const [name, domain] =
    site === 'platform'
      ? [PLATFORM_NAME, PLATFORM_HOST]
      : [PRODUCTS[site].name, PRODUCTS[site].mailDomain]
  return `${name} <${MAIL_LOCAL_PART}@${domain}>`
}

/**
 * The institutional product's name, overridable per deployment.
 *
 * Every existing call site reads this and means "the product in front of this
 * person", which on every one of them is ShulBase. A surface serving another
 * product asks `productName` instead.
 */
export const APP_NAME = envName ?? PRODUCTS.shulbase.name

/** Machine form: folders, database, storage prefixes, deploy targets. */
export const APP_SLUG = 'shulbase'

export const APP_TAGLINE = PRODUCTS.shulbase.tagline

/**
 * A namespaced key for browser storage, caches and events.
 *
 * Composed rather than written beside the name, so the prefix moves with it and
 * a stale key cannot outlive it. It also settles a separator that had drifted —
 * some keys used `:` and others `.`, which is invisible until you try to clear
 * them all.
 */
export const appKey = (...parts: string[]): string =>
  [APP_SLUG, ...parts].join(':')
